/**
 * Client-side model of the public event log.
 *
 * Everything here is computed from the unfiltered event feed alone, the same
 * way any independent monitor would: fingerprints are recomputed from the
 * published bytes, certificates and registration records are parsed locally
 * and signatures are verified against the registrar's trust anchor.
 */

import {
  SignatureCheck,
  TrustAnchor,
  verifyAccessCertificate,
  verifyRegistrationCertificate,
  verifyRegistrationRecord,
} from "./signature-verification";

export type EventType =
  "certificate_issued" | "certificate_revoked" | "registration_recorded";
export type CertificateType = "access_certificate" | "registration_certificate";
/** Art. 9 of (EU) 2025/848, as a registration record states it. */
export type RegistrationStatus = "active" | "suspended" | "cancelled";
export type WrpStatus = RegistrationStatus | "historic";

/**
 * The Insights derivation rules this replay implements (rules_version of a
 * snapshot). Version 2 takes the registration status and the WRP-level data
 * from registration records. Version 3 only changes how legacy purposes and
 * provided attestations of intended uses are read, which the monitor does not
 * compare.
 */
export const RULES_VERSION = 3;

/** `typ` of the registration records in the log. */
export const REGISTRATION_RECORD_TYPE = "wrp-registration+jwt";

const REGISTRATION_STATUSES: readonly unknown[] = [
  "active",
  "suspended",
  "cancelled",
];

export interface PublicEvent {
  event_id: string;
  sequence_number: string;
  event_type: EventType;
  effective_at?: string;
  published_at: string;
  wrp_id: string;
  certificate_fingerprint?: string;
  /** Present on revocation events. */
  revocation_reason?: string;
  certificate?: {
    media_type: "application/pkix-cert" | "application/jwt" | string;
    encoded: string;
  };
  /** Present on registration_recorded events: a compact JWS. */
  registration_record?: {
    media_type: "application/jwt" | string;
    encoded: string;
  };
}

export interface PublicEventPage {
  log_id: string;
  /** Changes when existing sequence numbers change meaning. */
  log_epoch: number;
  through_sequence: string;
  data: PublicEvent[];
  pagination: { next_after_sequence?: string | null };
}

/** Subset of the register statement used for the consistency check. */
export interface PublicWrp {
  wrp_id: string;
  display_name: string;
  status?: WrpStatus;
  certificates: Array<{
    certificate_fingerprint: string;
    status: "valid" | "expired" | "revoked";
  }>;
  /** In log order; the last one is the current registration. */
  registration_records?: Array<{
    record_fingerprint: string;
    registration_status: string;
    sequence_number: string;
  }>;
}

/** Subset of an /intended-uses entry used for the consistency check. */
export interface PublicIntendedUse {
  intended_use_id: string;
  wrp_id: string;
  certificate_fingerprints: string[];
}

/**
 * The register state an Insights response describes: the log replayed up to
 * as_of_sequence, with certificate expiry evaluated at evaluated_at. Both
 * together identify the state.
 */
export interface InsightsSnapshot {
  log_id: string;
  log_epoch: number;
  as_of_sequence: string;
  evaluated_at: string;
  rules_version: number;
}

/** A page of an Insights endpoint (/wrps, /intended-uses) at one log position. */
export interface PublicSnapshotPage<T> {
  snapshot: InsightsSnapshot;
  data: T[];
  pagination: { next_cursor: string | null };
}

/** GET /snapshot: the latest state the Insights API serves. */
export interface InsightsStatus {
  snapshot: InsightsSnapshot;
  /** Log head at the last synchronisation; the projection may lag behind. */
  log_head_sequence: string;
  synced_at?: string;
  next_change_at?: string;
}

/**
 * Registration certificate payload (ETSI TS 119 475 v1.2.1, clause 5.2.4) as
 * published, with the `claims` and `intermediary.name` spellings read as
 * `claim` and `sname`, like the registrar does. Published certificates are
 * not guaranteed to follow the schema, so nested values stay `unknown` until
 * a reader checks them.
 */
export interface RegistrationContent {
  sub?: string;
  name?: string;
  sub_ln?: string;
  sub_gn?: string;
  sub_fn?: string;
  country?: string;
  registry_uri?: string;
  info_uri?: string;
  support_uri?: string;
  privacy_policy?: string;
  entitlements?: string[];
  srv_description?: unknown;
  purpose?: unknown;
  credentials?: unknown;
  provides_attestations?: unknown;
  intermediary?: { sub?: string; sname?: string };
  /** Required with intermediary (GEN-5.2.4-09); act.sub names it again. */
  act?: { sub?: string };
  supervisory_authority?: { email?: string; phone?: string; uri?: string };
  public_body?: boolean;
  status?: { status_list?: { idx?: number; uri?: string } };
  iat?: number;
  exp?: number;
  jti?: string;
  intended_use_id?: string;
  [field: string]: unknown;
}

/** Payload fields the registrar reads: TS 119 475 v1.2.1 Tables 7 to 10, plus jti. */
export const REGISTRATION_FIELDS = new Set([
  "name",
  "sub_ln",
  "sub_gn",
  "sub_fn",
  "sub",
  "country",
  "registry_uri",
  "srv_description",
  "entitlements",
  "privacy_policy",
  "info_uri",
  "support_uri",
  "supervisory_authority",
  "policy_id",
  "certificate_policy",
  "jti",
  "iat",
  "status",
  "purpose",
  "credentials",
  "provides_attestations",
  "intermediary",
  "act",
  "public_body",
  "exp",
  "intended_use_id",
]);

/** Contents of an access certificate (X.509, ETSI TS 119 411-8). */
export interface AccessContent {
  commonName?: string;
  organizationName?: string;
  organizationIdentifier?: string;
  country?: string;
  issuer?: string;
  serialNumber?: string;
  notBefore?: string;
  notAfter?: string;
  uris: string[];
  emails: string[];
  phones: string[];
  dnsNames: string[];
  policies: string[];
  /** SHA-256 over the DER SubjectPublicKeyInfo. */
  publicKeyFingerprint?: string;
}

export interface CertificateRecord {
  fingerprint: string;
  type: CertificateType | "unknown";
  wrpId: string;
  issued: PublicEvent;
  revoked?: PublicEvent;
  registration?: RegistrationContent;
  access?: AccessContent;
  /** Intended use of a registration certificate, derived as the registrar does. */
  intendedUseId?: string;
  /** Fingerprint of the certificate this one replaced (revoked as superseded). */
  replaces?: string;
  /** Fingerprint of the certificate that replaced this one. */
  replacedBy?: string;
  /** Set when the published bytes could not be parsed. */
  parseError?: string;
  signature: SignatureCheck;
}

/**
 * Payload of a registration record: the registrar's signed statement of a
 * WRP's Annex I data and registration status. Like certificates, published
 * records are not guaranteed to follow the schema, so readers check every
 * value they use.
 */
export interface RegistrationRecordContent {
  /** The register identifier: the API base URL. */
  iss?: string;
  iat?: number;
  jti?: string;
  /** The wrp_id. */
  sub?: string;
  registration_status?: string;
  legal_name?: string;
  natural_person?: { given_name?: string; family_name?: string };
  display_name?: string;
  country?: string;
  identifiers?: Array<{
    semantic_identifier?: string;
    official_record?: {
      register?: string;
      authority?: string;
      country?: string;
      reference?: string;
    };
  }>;
  info_uri?: string;
  contact?: { support_uris?: string[]; emails?: string[]; phones?: string[] };
  service_descriptions?: unknown;
  public_body?: boolean;
  entitlements?: string[];
  sub_entitlements?: string[];
  intermediaries?: Array<{ wrp_id?: string; name?: string }>;
  registry_uri?: string;
  [field: string]: unknown;
}

/** A registration_recorded event with its decoded record. */
export interface RegistrationRecordEntry {
  /** SHA-256 over the UTF-8 compact JWS, like a registration certificate. */
  fingerprint: string;
  /** The WRP the event names; the record belongs to it. */
  wrpId: string;
  event: PublicEvent;
  /** Undefined when the record could not be decoded (see parseError). */
  payload?: RegistrationRecordContent;
  /** `typ` of the protected header, when it is a string. */
  type?: string;
  /** registration_status, when it is one of the Art. 9 values. */
  status?: RegistrationStatus;
  parseError?: string;
  signature: SignatureCheck;
}

/** Anomalies that are properties of the log itself, found while replaying. */
export interface LogAnomaly {
  kind:
    | "duplicate_event"
    | "sequence_not_increasing"
    | "sequence_gap"
    | "unknown_event_type"
    | "missing_artifact"
    | "missing_record"
    | "duplicate_issuance"
    | "duplicate_record"
    | "unknown_revocation"
    | "duplicate_revocation"
    | "revocation_wrp_mismatch";
  event: PublicEvent;
  related?: PublicEvent;
}

export interface LogReplay {
  events: PublicEvent[];
  certificates: Map<string, CertificateRecord>;
  /** Registration records by the wrp_id of their event, in log order. */
  registrations: Map<string, RegistrationRecordEntry[]>;
  anomalies: LogAnomaly[];
  /** False when no trust anchor was available; signatures are unchecked. */
  signaturesChecked: boolean;
}

export function compareSequence(left: string, right: string): number {
  const delta = BigInt(left) - BigInt(right);
  return delta === 0n ? 0 : delta > 0n ? 1 : -1;
}

/**
 * SHA-256 over the published artifact bytes: the DER for access certificates
 * and the UTF-8 compact JWS for registration certificates and registration
 * records. Matches the certificate_fingerprint of revocation events and the
 * register API.
 */
export async function fingerprint(certificate: {
  media_type: string;
  encoded: string;
}): Promise<string> {
  const bytes =
    certificate.media_type === "application/pkix-cert"
      ? base64ToBytes(certificate.encoded)
      : new TextEncoder().encode(certificate.encoded);
  return `sha-256:${await sha256Hex(bytes)}`;
}

/**
 * The registrar's intended-use identifier: the certificate's intended_use_id,
 * otherwise iu_ + the first 24 hex characters of SHA-256 over the canonical
 * JSON of wrp_id, purpose, credentials and provides_attestations.
 */
export async function intendedUseId(
  wrpId: string,
  registration: RegistrationContent,
): Promise<string> {
  if (registration.intended_use_id) {
    return registration.intended_use_id;
  }
  const digest = await sha256Hex(
    new TextEncoder().encode(
      canonicalJson({
        wrp_id: wrpId,
        purpose: registration.purpose ?? [],
        credentials: registration.credentials ?? [],
        provides_attestations: registration.provides_attestations ?? [],
      }),
    ),
  );
  return `iu_${digest.slice(0, 24)}`;
}

/** JSON with recursively sorted object keys, for reproducible digests. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const object = value as Record<string, unknown>;
    return `{${Object.keys(object)
      .filter((key) => object[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(object[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

/** Header and payload of a compact JWS exactly as published. */
export function decodeJws(jws: string): { header: unknown; payload: unknown } {
  const [header, payload] = jws.split(".");
  if (!header || !payload) {
    throw new Error("Not a compact JWS");
  }
  return { header: decodeJsonPart(header), payload: decodeJsonPart(payload) };
}

/**
 * Replays the events in log order: derives the certificate lifecycle and
 * collects the registration records of every WRP.
 */
export async function replay(
  events: PublicEvent[],
  trustAnchor?: TrustAnchor,
): Promise<LogReplay> {
  const certificates = new Map<string, CertificateRecord>();
  const registrations = new Map<string, RegistrationRecordEntry[]>();
  const recorded = new Map<string, RegistrationRecordEntry>();
  const anomalies: LogAnomaly[] = [];
  const seenEvents = new Set<string>();
  let previous: PublicEvent | undefined;

  for (const event of events) {
    if (seenEvents.has(event.event_id)) {
      anomalies.push({ kind: "duplicate_event", event });
    }
    seenEvents.add(event.event_id);
    if (
      previous &&
      compareSequence(event.sequence_number, previous.sequence_number) <= 0
    ) {
      anomalies.push({
        kind: "sequence_not_increasing",
        event,
        related: previous,
      });
    } else if (
      // The unfiltered log is gap-free: it starts at 1 and counts up by one.
      BigInt(event.sequence_number) >
      BigInt(previous?.sequence_number ?? "0") + 1n
    ) {
      anomalies.push({ kind: "sequence_gap", event, related: previous });
    }
    previous = event;

    if (event.event_type === "certificate_issued") {
      if (!event.certificate) {
        anomalies.push({ kind: "missing_artifact", event });
        continue;
      }
      const print = await fingerprint(event.certificate);
      const existing = certificates.get(print);
      if (existing) {
        anomalies.push({
          kind: "duplicate_issuance",
          event,
          related: existing.issued,
        });
        continue;
      }
      const record = await parseCertificate(print, event);
      if (trustAnchor) {
        record.signature = await verifySignature(record, trustAnchor);
      }
      certificates.set(print, record);
      continue;
    }

    if (event.event_type === "registration_recorded") {
      if (!event.registration_record) {
        anomalies.push({ kind: "missing_record", event });
        continue;
      }
      const entry = parseRegistrationRecord(
        await fingerprint(event.registration_record),
        event,
      );
      // The register keeps a repeated record too, so it stays in the list.
      const earlier = recorded.get(entry.fingerprint);
      if (earlier) {
        anomalies.push({
          kind: "duplicate_record",
          event,
          related: earlier.event,
        });
      } else {
        recorded.set(entry.fingerprint, entry);
      }
      if (trustAnchor) {
        entry.signature = await verifyRegistrationRecord(
          event.registration_record.encoded,
          trustAnchor,
        );
      }
      registrations.set(event.wrp_id, [
        ...(registrations.get(event.wrp_id) ?? []),
        entry,
      ]);
      continue;
    }

    if (event.event_type !== "certificate_revoked") {
      anomalies.push({ kind: "unknown_event_type", event });
      continue;
    }

    const issued = event.certificate_fingerprint
      ? certificates.get(event.certificate_fingerprint)
      : undefined;
    if (!issued) {
      anomalies.push({ kind: "unknown_revocation", event });
    } else if (issued.revoked) {
      anomalies.push({
        kind: "duplicate_revocation",
        event,
        related: issued.revoked,
      });
    } else {
      if (issued.wrpId !== event.wrp_id) {
        anomalies.push({
          kind: "revocation_wrp_mismatch",
          event,
          related: issued.issued,
        });
      }
      issued.revoked = event;
    }
  }

  linkReplacements([...certificates.values()]);
  return {
    events,
    certificates,
    registrations,
    anomalies,
    signaturesChecked: Boolean(trustAnchor),
  };
}

/**
 * Pairs every certificate revoked as superseded with its replacement: a
 * certificate of the same WRP and type issued after the predecessor and
 * before its revocation. One with the same intended use (registration) or
 * the same public key (access) is preferred, otherwise the last one issued
 * before the revocation. Each certificate replaces at most one other.
 */
function linkReplacements(certificates: CertificateRecord[]): void {
  const groups = new Map<string, CertificateRecord[]>();
  for (const record of certificates) {
    const key = `${record.wrpId}\n${record.type}`;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }

  for (const records of groups.values()) {
    const superseded = records
      .filter((record) => record.revoked?.revocation_reason === "superseded")
      .sort((left, right) =>
        compareSequence(
          left.revoked!.sequence_number,
          right.revoked!.sequence_number,
        ),
      );
    for (const predecessor of superseded) {
      const candidates = records
        .filter(
          (other) =>
            !other.replaces &&
            compareSequence(
              other.issued.sequence_number,
              predecessor.issued.sequence_number,
            ) > 0 &&
            compareSequence(
              other.issued.sequence_number,
              predecessor.revoked!.sequence_number,
            ) < 0,
        )
        .sort((left, right) =>
          compareSequence(
            right.issued.sequence_number,
            left.issued.sequence_number,
          ),
        );
      const successor =
        candidates.find((candidate) => sameSubject(candidate, predecessor)) ??
        candidates[0];
      if (successor) {
        successor.replaces = predecessor.fingerprint;
        predecessor.replacedBy = successor.fingerprint;
      }
    }
  }
}

function sameSubject(left: CertificateRecord, right: CertificateRecord) {
  return left.type === "registration_certificate"
    ? left.intendedUseId === right.intendedUseId
    : Boolean(left.access?.publicKeyFingerprint) &&
        left.access?.publicKeyFingerprint ===
          right.access?.publicKeyFingerprint;
}

/**
 * The register status as the Insights service derives it (rule 5, since
 * rules version 2): the registration_status of the WRP's current registration
 * record. A WRP without one, or whose current record states no Art. 9
 * status, falls back to its certificates: active while a certificate is
 * unrevoked; otherwise the reason of the most recent revocation tells
 * suspended or cancelled apart from historic.
 */
export function wrpStatus(
  records: CertificateRecord[],
  registrations: RegistrationRecordEntry[] = [],
): WrpStatus {
  const recorded = currentRegistration(registrations)?.status;
  if (recorded) {
    return recorded;
  }
  if (records.some((record) => !record.revoked)) {
    return "active";
  }
  const lastRevocation = records
    .map((record) => record.revoked!)
    .sort((left, right) =>
      compareSequence(right.sequence_number, left.sequence_number),
    )[0];
  switch (lastRevocation?.revocation_reason) {
    case "registration_suspended":
      return "suspended";
    case "registration_cancelled":
      return "cancelled";
    default:
      return "historic";
  }
}

/**
 * The current registration: the latest record that could be decoded (rule
 * 3). Records that cannot be decoded are not part of the register.
 */
export function currentRegistration(
  registrations: RegistrationRecordEntry[],
): RegistrationRecordEntry | undefined {
  return [...registrations].reverse().find((entry) => entry.payload);
}

function verifySignature(
  record: CertificateRecord,
  trustAnchor: TrustAnchor,
): Promise<SignatureCheck> {
  const encoded = record.issued.certificate!.encoded;
  if (record.type === "access_certificate") {
    return verifyAccessCertificate(encoded, trustAnchor);
  }
  if (record.type === "registration_certificate") {
    return verifyRegistrationCertificate(encoded, trustAnchor);
  }
  return Promise.resolve("unsupported");
}

/**
 * Display name as the register derives it (rule 4): the display_name of the
 * current registration record, else the name of the preferred registration
 * certificate, else the common name of the preferred access certificate.
 */
export function displayNames(replayed: LogReplay): Map<string, string> {
  const byWrp = new Map<string, CertificateRecord[]>();
  for (const record of replayed.certificates.values()) {
    byWrp.set(record.wrpId, [...(byWrp.get(record.wrpId) ?? []), record]);
  }
  const names = new Map<string, string>();
  for (const wrpId of new Set([
    ...byWrp.keys(),
    ...replayed.registrations.keys(),
  ])) {
    const records = byWrp.get(wrpId) ?? [];
    const name = [
      currentRegistration(replayed.registrations.get(wrpId) ?? [])?.payload
        ?.display_name,
      preferred(records.filter((record) => record.registration))?.registration
        ?.name,
      preferred(records.filter((record) => record.access))?.access?.commonName,
    ].find((value) => typeof value === "string" && value.length > 0);
    if (name) {
      names.set(wrpId, name);
    }
  }
  return names;
}

/**
 * The certificate WRP data is read from: the latest unrevoked one by
 * issuance, else the latest. The register also skips expired certificates,
 * which the monitor does not evaluate.
 */
function preferred(
  records: CertificateRecord[],
): CertificateRecord | undefined {
  const ordered = [...records].sort((left, right) =>
    compareSequence(right.issued.sequence_number, left.issued.sequence_number),
  );
  return ordered.find((record) => !record.revoked) ?? ordered[0];
}

async function parseCertificate(
  print: string,
  event: PublicEvent,
): Promise<CertificateRecord> {
  const record: CertificateRecord = {
    fingerprint: print,
    type: "unknown",
    wrpId: event.wrp_id,
    issued: event,
    signature: "unchecked",
  };
  const certificate = event.certificate!;
  try {
    if (certificate.media_type === "application/jwt") {
      record.type = "registration_certificate";
      record.registration = normalizeRegistration(
        decodeJws(certificate.encoded).payload,
      );
      record.intendedUseId = await intendedUseId(
        record.wrpId,
        record.registration,
      );
    } else if (certificate.media_type === "application/pkix-cert") {
      record.type = "access_certificate";
      record.access = await readAccessCertificate(
        base64ToBytes(certificate.encoded),
      );
    }
  } catch (error) {
    record.parseError =
      error instanceof Error ? error.message : "Unreadable certificate";
  }
  return record;
}

/**
 * Decodes a registration record as the register does: a compact JWS whose
 * payload is a JSON object. The header is only read for the type check, so
 * an unreadable header does not make the record undecodable.
 */
function parseRegistrationRecord(
  print: string,
  event: PublicEvent,
): RegistrationRecordEntry {
  const entry: RegistrationRecordEntry = {
    fingerprint: print,
    wrpId: event.wrp_id,
    event,
    signature: "unchecked",
  };
  const parts = event.registration_record!.encoded.split(".");
  try {
    if (parts.length !== 3) {
      throw new Error("Not a compact JWS");
    }
    const payload = decodeJsonPart(parts[1]);
    if (!isRecord(payload)) {
      throw new Error("The payload is not a JSON object");
    }
    entry.payload = payload as RegistrationRecordContent;
    const status = payload["registration_status"];
    entry.status = REGISTRATION_STATUSES.includes(status)
      ? (status as RegistrationStatus)
      : undefined;
  } catch (error) {
    entry.parseError =
      error instanceof Error ? error.message : "Unreadable registration record";
    return entry;
  }
  try {
    const header = decodeJsonPart(parts[0]);
    entry.type =
      isRecord(header) && typeof header["typ"] === "string"
        ? header["typ"]
        : undefined;
  } catch {
    // No type: reported like a wrong one.
  }
  return entry;
}

/**
 * Accepts the `claims` and `intermediary.name` spellings next to the
 * `claim` and `sname` of TS 119 475 v1.2.1, as the registrar's parser does
 * (normalizeWRPRCPayload of @owf/eudi-wrprc). A spelling is only renamed when
 * the target name is not present as well.
 */
function normalizeRegistration(payload: unknown): RegistrationContent {
  if (!isRecord(payload)) {
    throw new Error("The payload is not a JSON object");
  }
  const result: Record<string, unknown> = { ...payload };
  if (Array.isArray(result["credentials"])) {
    result["credentials"] = result["credentials"].map((entry) =>
      renameKey(entry, "claims", "claim"),
    );
  }
  if ("intermediary" in result) {
    result["intermediary"] = renameKey(result["intermediary"], "name", "sname");
  }
  return result as RegistrationContent;
}

function renameKey(value: unknown, from: string, to: string): unknown {
  if (!isRecord(value) || !(from in value) || to in value) {
    return value;
  }
  const { [from]: moved, ...rest } = value;
  return { ...rest, [to]: moved };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function decodeJsonPart(part: string): unknown {
  return JSON.parse(
    new TextDecoder().decode(
      base64ToBytes(part.replace(/-/g, "+").replace(/_/g, "/")),
    ),
  );
}

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.padEnd(
    value.length + ((4 - (value.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

// --- Minimal DER reader for X.509 access certificates ---------------------------

interface Tlv {
  tag: number;
  headerStart: number;
  start: number;
  end: number;
}

const OID_COMMON_NAME = "2.5.4.3";
const OID_COUNTRY = "2.5.4.6";
const OID_ORGANIZATION = "2.5.4.10";
const OID_ORGANIZATION_IDENTIFIER = "2.5.4.97";
const OID_TELEPHONE_NUMBER = "2.5.4.20";
const OID_SUBJECT_ALT_NAME = "2.5.29.17";
const OID_CERTIFICATE_POLICIES = "2.5.29.32";

function readTlv(bytes: Uint8Array, offset: number): Tlv {
  const tag = bytes[offset];
  let length = bytes[offset + 1];
  let start = offset + 2;
  if (length & 0x80) {
    const count = length & 0x7f;
    length = 0;
    for (let index = 0; index < count; index++) {
      length = length * 256 + bytes[start + index];
    }
    start += count;
  }
  if (start + length > bytes.length) {
    throw new Error("Truncated DER");
  }
  return { tag, headerStart: offset, start, end: start + length };
}

function children(bytes: Uint8Array, parent: Tlv): Tlv[] {
  const result: Tlv[] = [];
  for (let offset = parent.start; offset < parent.end;) {
    const tlv = readTlv(bytes, offset);
    result.push(tlv);
    offset = tlv.end;
  }
  return result;
}

function decodeOid(bytes: Uint8Array): string {
  const parts = [Math.floor(bytes[0] / 40), bytes[0] % 40];
  let value = 0;
  for (let index = 1; index < bytes.length; index++) {
    value = value * 128 + (bytes[index] & 0x7f);
    if (!(bytes[index] & 0x80)) {
      parts.push(value);
      value = 0;
    }
  }
  return parts.join(".");
}

function text(der: Uint8Array, tlv: Tlv): string {
  return new TextDecoder().decode(der.subarray(tlv.start, tlv.end));
}

function oid(der: Uint8Array, tlv: Tlv): string {
  return decodeOid(der.subarray(tlv.start, tlv.end));
}

/**
 * Reads subject, issuer, serial number, validity, subjectAltName, policies
 * and the key fingerprint of an access certificate.
 */
async function readAccessCertificate(der: Uint8Array<ArrayBuffer>) {
  const tbs = children(der, readTlv(der, 0))[0];
  const fields = children(der, tbs);
  // tbsCertificate: [0] version?, serial, signature, issuer, validity,
  // subject, subjectPublicKeyInfo, [1]?, [2]?, [3] extensions?
  const offset = fields[0].tag === 0xa0 ? 1 : 0;
  const [serial, , issuer, validity, subject, spki] = fields.slice(offset);
  const subjectName = readName(der, subject);
  const issuerName = readName(der, issuer);
  const [notBefore, notAfter] = children(der, validity).map((time) =>
    readTime(der, time),
  );
  const result: AccessContent = {
    commonName: subjectName[OID_COMMON_NAME],
    organizationName: subjectName[OID_ORGANIZATION],
    organizationIdentifier: subjectName[OID_ORGANIZATION_IDENTIFIER],
    country: subjectName[OID_COUNTRY],
    issuer: issuerName[OID_COMMON_NAME] ?? issuerName[OID_ORGANIZATION],
    serialNumber: Array.from(der.subarray(serial.start, serial.end), (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join(":"),
    notBefore,
    notAfter,
    uris: [],
    emails: [],
    phones: [],
    dnsNames: [],
    policies: [],
    publicKeyFingerprint: `sha-256:${await sha256Hex(
      der.slice(spki.headerStart, spki.end),
    )}`,
  };

  const wrapper = fields.find((field) => field.tag === 0xa3);
  const extensions = wrapper ? children(der, children(der, wrapper)[0]) : [];
  for (const extension of extensions) {
    const parts = children(der, extension);
    const value = readTlv(der, parts[parts.length - 1].start);
    switch (oid(der, parts[0])) {
      case OID_SUBJECT_ALT_NAME:
        readGeneralNames(der, value, result);
        break;
      case OID_CERTIFICATE_POLICIES:
        for (const policy of children(der, value)) {
          result.policies.push(oid(der, children(der, policy)[0]));
        }
        break;
    }
  }
  return result;
}

/** Attribute values of a distinguished name by OID (last value wins). */
function readName(der: Uint8Array, name: Tlv): Record<string, string> {
  const result: Record<string, string> = {};
  for (const rdn of children(der, name)) {
    for (const attribute of children(der, rdn)) {
      const [type, value] = children(der, attribute);
      result[oid(der, type)] = text(der, value);
    }
  }
  return result;
}

/** UTCTime (YYMMDDHHMMSSZ) or GeneralizedTime (YYYYMMDDHHMMSSZ) as ISO 8601. */
function readTime(der: Uint8Array, time: Tlv): string | undefined {
  const value = text(der, time);
  const match = value.match(
    /^(\d{2}|\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})Z$/,
  );
  if (!match) return undefined;
  const year =
    match[1].length === 4
      ? match[1]
      : `${Number(match[1]) < 50 ? "20" : "19"}${match[1]}`;
  return `${year}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}Z`;
}

/** rfc822Name [1], dNSName [2], URI [6] and telephoneNumber otherName [0]. */
function readGeneralNames(
  der: Uint8Array,
  names: Tlv,
  result: AccessContent,
): void {
  for (const name of children(der, names)) {
    if (name.tag === 0x81) result.emails.push(text(der, name));
    if (name.tag === 0x82) result.dnsNames.push(text(der, name));
    if (name.tag === 0x86) result.uris.push(text(der, name));
    if (name.tag === 0xa0) {
      const [typeId, explicit] = children(der, name);
      if (explicit && oid(der, typeId) === OID_TELEPHONE_NUMBER) {
        result.phones.push(text(der, readTlv(der, explicit.start)));
      }
    }
  }
}
